import { Directive, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { SaveResidentFeedback } from '../../../+state';
import { StoreReviewService } from '../../../services/feedback';
import {
  FEEDBACK_APP_VERSION_MAX_LENGTH,
  FEEDBACK_CATEGORY_OPTIONS,
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_OS_VERSION_MAX_LENGTH,
  FeedbackPromptStep,
  IFeedbackCategoryOption,
  IResidentFeedbackContext,
  IResidentFeedbackInput,
  ResidentFeedbackCategory,
  ResidentFeedbackResult,
} from '../../../models';

/**
 * Upper bound on waiting for the store review flow before showing the thank-you
 * step anyway. Only Android can actually take this long; iOS resolves at once.
 */
const STORE_REVIEW_WAIT_TIMEOUT_MS = 8000;

/**
 * All logic for the in-app feedback sheet (ART-19551). Platform components
 * supply only a template plus `closeSheet()` and `context`.
 */
@Directive()
export abstract class FeedbackPromptBaseComponent {
  protected readonly store = inject(Store);
  protected readonly storeReview = inject(StoreReviewService);

  public readonly steps = FeedbackPromptStep;
  public readonly categoryOptions: IFeedbackCategoryOption[] = FEEDBACK_CATEGORY_OPTIONS;
  public readonly commentMaxLength = FEEDBACK_COMMENT_MAX_LENGTH;

  public step: FeedbackPromptStep = FeedbackPromptStep.PROMPT;
  /** Set as soon as an outcome is recorded, so exactly one is ever sent. */
  private hasAnswered = false;
  public selectedCategory: ResidentFeedbackCategory | null = null;
  public comment = '';

  /** Device/app metadata. The platform component provides it; never read natively here. */
  protected abstract get context(): IResidentFeedbackContext;

  /**
   * Dismiss the presented sheet, optionally handing a result back to whoever
   * presented it. NS forwards this to `ModalDialogParams.closeCallback`, which
   * resolves the presenter's `showModal` promise.
   *
   * Follow-up actions belong to the presenter, not here: navigating while the
   * sheet is still dismissing gets undone when the modal tears down and restores
   * the underlying frame.
   */
  protected abstract closeSheet(result?: unknown): void;

  /**
   * Trim, then cut to `max`, then collapse an empty result to null.
   *
   * `saveFeedback` answers `true` or a GraphQL error — never `false` — and an
   * over-long field is one of the ways it errors. Clamping here keeps the four
   * `ERROR_FEEDBACK_*` cases unreachable from the client.
   */
  private static clamp(value: string | null | undefined, max: number): string | null {
    const trimmed = value?.trim() ?? '';
    return trimmed.length > 0 ? trimmed.slice(0, max) : null;
  }

  /**
   * The single choke point for the mutation, so every outgoing field is clamped
   * here rather than at its source — a future caller cannot route around it.
   * `comment` is already capped as it is typed; re-clamping costs nothing and
   * removes the assumption that it was.
   */
  private save(
    result: ResidentFeedbackResult,
    category: ResidentFeedbackCategory | null = null,
    comment: string | null = null
  ): void {
    this.hasAnswered = true;
    const { platform, osVersion, appVersion } = this.context;
    const input: IResidentFeedbackInput = {
      result,
      category,
      comment: FeedbackPromptBaseComponent.clamp(comment, FEEDBACK_COMMENT_MAX_LENGTH),
      platform,
      osVersion: FeedbackPromptBaseComponent.clamp(osVersion, FEEDBACK_OS_VERSION_MAX_LENGTH),
      appVersion: FeedbackPromptBaseComponent.clamp(appVersion, FEEDBACK_APP_VERSION_MAX_LENGTH),
    };
    this.store.dispatch(SaveResidentFeedback({ input }));
  }

  public get commentLength(): number {
    return this.comment.length;
  }

  /**
   * Spec FR-6 / plan decision D2 — the LITERAL AC reading: both fields are
   * optional, and the comment only becomes mandatory once a category is picked.
   *
   * This INTENTIONALLY differs from Figma, which greys the button out when both
   * fields are empty. Do not "fix" it to match the design without reopening D2.
   */
  public get canSubmit(): boolean {
    return !this.selectedCategory || this.comment.trim().length > 0;
  }

  /** Tapping the selected chip clears it — the design has no other clear control. */
  public selectCategory(category: ResidentFeedbackCategory): void {
    this.selectedCategory = this.selectedCategory === category ? null : category;
  }

  public onCommentChange(value: string): void {
    this.comment = (value ?? '').slice(0, this.commentMaxLength);
  }

  /**
   * AC 2a/2b/2c: record the positive answer, ask the OS for a store review, then
   * show the thank-you step once that flow is done.
   *
   * How much "once that flow is done" is worth depends on the platform:
   *
   * - **Android** — Play's `launchReviewFlow` completes when the dialog closes (or
   *   immediately if none was shown), so the thank-you genuinely follows it.
   * - **iOS** — `SKStoreReviewController.requestReview` returns void with no
   *   callback. Apple never reveals whether a dialog appeared or when it was
   *   dismissed, so the promise resolves straight away and the dialog is drawn
   *   over the thank-you step. That is not something this code can fix.
   *
   * The wait is capped so a store flow that never reports back cannot strand the
   * resident on the prompt step.
   */
  public onPositive(): void {
    this.save(ResidentFeedbackResult.POSITIVE);
    void this.showThanksAfterReview();
  }

  private async showThanksAfterReview(): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.storeReview.requestReview(),
        new Promise<void>(resolve => {
          timeoutId = setTimeout(resolve, STORE_REVIEW_WAIT_TIMEOUT_MS);
        }),
      ]);
    } finally {
      // `finally` so a rejected or hanging store flow still advances the sheet.
      clearTimeout(timeoutId);
      this.step = FeedbackPromptStep.THANK_YOU;
    }
  }

  public onNegative(): void {
    this.step = FeedbackPromptStep.NEGATIVE_FORM;
  }

  public onSubmit(): void {
    if (!this.canSubmit) {
      return;
    }
    const comment = this.comment.trim();
    this.save(
      ResidentFeedbackResult.NEGATIVE,
      this.selectedCategory,
      comment.length > 0 ? comment : null
    );
    this.step = FeedbackPromptStep.THANK_YOU;
  }

  /**
   * AC 4a: record that the resident saw the sheet. Decision D6 — a half-filled
   * form is not a submitted opinion, so this is DISMISSED, never a partial
   * NEGATIVE.
   *
   * Guarded on `hasAnswered`, not on the step. After a positive answer the sheet
   * sits on `PROMPT` until the store review flow settles, and closing it in that
   * window used to send DISMISSED on top of the POSITIVE already recorded —
   * two contradictory outcomes for one prompt.
   */
  public onDismiss(result?: unknown): void {
    if (!this.hasAnswered) {
      this.save(ResidentFeedbackResult.DISMISSED);
    }
    this.closeSheet(result);
  }

  public onDone(): void {
    this.closeSheet();
  }
}
